import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";

interface SEOHeadProps {
  title?: string;
  description?: string;
}

// Base URL for the site (update for production)
const BASE_URL = "https://www.lacavagrecque.com";

// Default meta descriptions by language
const DEFAULT_DESCRIPTIONS = {
  fr: "LA CAVA GRECQUE - Restaurant grec familial depuis 1989. Apportez votre vin! Souvlaki, gyros, moussaka et plats traditionnels. Événements et groupes bienvenus à Montréal et Delson.",
  en: "LA CAVA GRECQUE - Family Greek restaurant since 1989. Bring your own wine! Souvlaki, gyros, moussaka and traditional dishes. Events and groups welcome in Montréal and Delson.",
};

// Page-specific titles by language
const PAGE_TITLES: Record<string, { fr: string; en: string }> = {
  "/": {
    fr: "LA CAVA GRECQUE - Restaurant Grec Familial Depuis 1989 | Apportez Votre Vin | Montréal & Delson",
    en: "LA CAVA GRECQUE - Family Greek Restaurant Since 1989 | BYOW | Montréal & Delson",
  },
  "/montreal": {
    fr: "LA CAVA GRECQUE Montréal - Restaurant Grec Familial | Apportez Votre Vin",
    en: "LA CAVA GRECQUE Montreal - Family Greek Restaurant | BYOW",
  },
  "/delson": {
    fr: "LA CAVA GRECQUE Delson - Restaurant Grec Familial | Apportez Votre Vin",
    en: "LA CAVA GRECQUE Delson - Family Greek Restaurant | BYOW",
  },
  "/order": {
    fr: "Commander - LA CAVA GRECQUE | Pour Emporter et Livraison",
    en: "Order - LA CAVA GRECQUE | Pickup and Delivery",
  },
  "/reservations": {
    fr: "Réservations & Événements - LA CAVA GRECQUE | Groupes et Célébrations",
    en: "Reservations & Events - LA CAVA GRECQUE | Groups and Celebrations",
  },
};

// Menu section titles
const MENU_SECTION_TITLES: Record<string, { fr: string; en: string }> = {
  lunch: { fr: "Menu Midi", en: "Lunch Menu" },
  dinner: { fr: "Menu Principal", en: "Dinner Menu" },
  "new-arrivals": { fr: "Nouveautés", en: "New Arrivals" },
  specials: { fr: "Spéciales", en: "Specials" },
  grocery: { fr: "Épicerie", en: "Grocery" },
};

const SEOHead: React.FC<SEOHeadProps> = ({ title, description }) => {
  const location = useLocation();
  const { i18n } = useTranslation();
  const currentLang = i18n.language as "en" | "fr";

  useEffect(() => {
    const pathname = location.pathname;

    // Determine the page title
    let pageTitle = title;
    if (!pageTitle) {
      // Check for exact match first
      if (PAGE_TITLES[pathname]) {
        pageTitle = PAGE_TITLES[pathname][currentLang];
      }
      // Check for menu pages
      else if (pathname.includes("/menu/")) {
        const parts = pathname.split("/");
        const locationName = parts[1]; // montreal or delson
        const section = parts[3]; // lunch, dinner, etc.

        const locationTitle =
          locationName === "montreal" ? "Montréal" : "Delson";
        const sectionTitle =
          MENU_SECTION_TITLES[section]?.[currentLang] || section;

        pageTitle =
          currentLang === "fr"
            ? `${sectionTitle} - LA CAVA GRECQUE ${locationTitle}`
            : `${sectionTitle} - LA CAVA GRECQUE ${locationTitle}`;
      }
      // Default title
      else {
        pageTitle =
          currentLang === "fr"
            ? "LA CAVA GRECQUE - Restaurant Grec Familial Depuis 1989 | Apportez Votre Vin"
            : "LA CAVA GRECQUE - Family Greek Restaurant Since 1989 | BYOW";
      }
    }

    // Set the document title
    document.title = pageTitle;

    // Update or create meta description
    const metaDescription = description || DEFAULT_DESCRIPTIONS[currentLang];
    let descriptionMeta = document.querySelector(
      'meta[name="description"]:not([lang])',
    ) as HTMLMetaElement | null;

    if (!descriptionMeta) {
      descriptionMeta = document.createElement("meta");
      descriptionMeta.name = "description";
      document.head.appendChild(descriptionMeta);
    }
    descriptionMeta.content = metaDescription;

    // Update canonical URL
    const canonicalUrl = `${BASE_URL}${pathname}`;
    let canonicalLink = document.querySelector(
      'link[rel="canonical"]',
    ) as HTMLLinkElement | null;

    if (!canonicalLink) {
      canonicalLink = document.createElement("link");
      canonicalLink.rel = "canonical";
      document.head.appendChild(canonicalLink);
    }
    canonicalLink.href = canonicalUrl;

    // Update hreflang tags (same URL for both languages since we don't use URL-based i18n)
    const updateHreflang = (lang: string, href: string) => {
      let link = document.querySelector(
        `link[rel="alternate"][hreflang="${lang}"]`,
      ) as HTMLLinkElement | null;

      if (!link) {
        link = document.createElement("link");
        link.rel = "alternate";
        link.hreflang = lang;
        document.head.appendChild(link);
      }
      link.href = href;
    };

    updateHreflang("fr", canonicalUrl);
    updateHreflang("en", canonicalUrl);
    updateHreflang("x-default", canonicalUrl);

    // Update Open Graph URL
    let ogUrl = document.querySelector(
      'meta[property="og:url"]',
    ) as HTMLMetaElement | null;

    if (!ogUrl) {
      ogUrl = document.createElement("meta");
      ogUrl.setAttribute("property", "og:url");
      document.head.appendChild(ogUrl);
    }
    ogUrl.content = canonicalUrl;

    // Update OG title
    let ogTitle = document.querySelector(
      'meta[property="og:title"]',
    ) as HTMLMetaElement | null;

    if (!ogTitle) {
      ogTitle = document.createElement("meta");
      ogTitle.setAttribute("property", "og:title");
      document.head.appendChild(ogTitle);
    }
    ogTitle.content = pageTitle;

    // Update OG description
    let ogDesc = document.querySelector(
      'meta[property="og:description"]',
    ) as HTMLMetaElement | null;

    if (!ogDesc) {
      ogDesc = document.createElement("meta");
      ogDesc.setAttribute("property", "og:description");
      document.head.appendChild(ogDesc);
    }
    ogDesc.content = metaDescription;

    // Update OG locale based on current language
    let ogLocale = document.querySelector(
      'meta[property="og:locale"]',
    ) as HTMLMetaElement | null;

    if (!ogLocale) {
      ogLocale = document.createElement("meta");
      ogLocale.setAttribute("property", "og:locale");
      document.head.appendChild(ogLocale);
    }
    ogLocale.content = currentLang === "fr" ? "fr_CA" : "en_CA";

    // Update geo.placename based on the page
    let geoPlacename = document.querySelector(
      'meta[name="geo.placename"]',
    ) as HTMLMetaElement | null;

    if (!geoPlacename) {
      geoPlacename = document.createElement("meta");
      geoPlacename.name = "geo.placename";
      document.head.appendChild(geoPlacename);
    }

    // Determine location-specific placename
    if (pathname.startsWith("/montreal")) {
      geoPlacename.content = "Montreal";
    } else if (pathname.startsWith("/delson")) {
      geoPlacename.content = "Delson";
    } else {
      // For general pages (home, order, reservations), show both locations
      geoPlacename.content = "Montreal, Delson";
    }
  }, [location.pathname, currentLang, title, description]);

  // This component doesn't render anything visible
  return null;
};

export default SEOHead;
